import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {FormData | null} */
  #savedFormState = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Saves the current form state inside the dialog.
   */
  #saveFormState() {
    const { dialog } = this.refs;
    const form = dialog.querySelector('form');
    if (form) {
      this.#savedFormState = new FormData(form);
    }
  }

  /**
   * Restores the saved form state inside the dialog.
   * @returns {boolean} Whether the form state was restored
   */
  #restoreFormState() {
    const { dialog } = this.refs;
    const form = dialog.querySelector('form');
    if (!form || !this.#savedFormState) return false;

    // Collect all values for each field name (handles multiple checkboxes with same name)
    /** @type {Map<string, string[]>} */
    const valuesByName = new Map();
    for (const [name, value] of this.#savedFormState.entries()) {
      if (!valuesByName.has(name)) {
        valuesByName.set(name, []);
      }
      valuesByName.get(name)?.push(String(value));
    }

    // Uncheck all checkboxes first
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    });

    // Restore saved values
    for (const [name, values] of valuesByName.entries()) {
      const inputs = form.querySelectorAll(`[name="${name}"]`);
      inputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          if (input.type === 'checkbox' || input.type === 'radio') {
            // Check if input's value is in the saved values array
            input.checked = values.includes(input.value);
          } else {
            input.value = values[0] || '';
          }
        } else if (input instanceof HTMLSelectElement) {
          input.value = values[0] || '';
        }
      });
    }

    this.#savedFormState = null;
    return true;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#saveFormState();

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Shows the dialog and expands a specific filter accordion.
   * @param {Event} event - The click event from the filter button
   */
  showDialogWithFilter(event) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#saveFormState();

    // Get the filter param name from the clicked button
    const button = event?.target instanceof Element ? event.target.closest('[data-filter-param-name]') : null;
    const filterParamName = button?.getAttribute('data-filter-param-name');

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Close all accordions and open only the target one
      if (filterParamName) {
        const accordions = dialog.querySelectorAll('accordion-custom[data-filter-param-name]');
        accordions.forEach((accordion) => {
          const details = accordion.querySelector('details');
          if (details) {
            const isTarget = accordion.getAttribute('data-filter-param-name') === filterParamName;
            details.open = isTarget;
          }
        });
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog (applies changes).
   */
  closeDialog = async () => {
    this.#savedFormState = null;
    await this.#closeDialogInternal();
  };

  /**
   * Cancels and closes the dialog (restores previous state).
   */
  cancelDialog = async () => {
    const didRestore = this.#restoreFormState();
    await this.#closeDialogInternal();

    // If form state was restored, trigger filter update to sync URL and results
    if (didRestore) {
      const { dialog } = this.refs;
      const facetsForm = /** @type {any} */ (dialog.querySelector('facets-form-component'));
      if (facetsForm && typeof facetsForm.updateFilters === 'function') {
        facetsForm.updateFilters();
      }
    }
  };

  /**
   * Internal method to close the dialog.
   */
  #closeDialogInternal = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.cancelDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.cancelDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
